import type { Element } from 'hast';
import type { ShortcodeMap } from '@nuxt-shared/lib/cms-shortcodes';
import type { HtmlVNodeChildren, ReplaceResult } from '@nuxt-shared/lib/html-to-vnodes';

/**
 * Правило замены элемента редакторского HTML на компонент.
 *
 * Нужно там, где место вставки задано не шорткодом, а самим тегом с
 * data-атрибутами (`<div data-charts="am_fund_11">` конструктора графиков).
 * Компонент приносит страница: `shared/` не может импортировать `widgets/`.
 */
export interface CmsElementReplacer {
  /** Подходит ли элемент под это правило. */
  match: (el: Element) => boolean;
  /**
   * Чем заменить. `undefined` — элемент рендерится как обычный HTML;
   * `renderChildren()` конвертирует потомков по общим правилам.
   */
  render: (el: Element, renderChildren: () => HtmlVNodeChildren) => ReplaceResult;
}

/**
 * Сводит список правил в один реплейсер для `htmlToVNodes`: правила
 * проверяются по порядку, побеждает первое, вернувшее не `undefined`.
 *
 * Нужен и `CmsContent`, и конвертеру лендингов — поэтому живёт рядом с типом,
 * а не копируется в оба места.
 */
export function applyElementReplacers(
  replacers: CmsElementReplacer[],
  el: Element,
  renderChildren: () => HtmlVNodeChildren,
): ReplaceResult {
  for (const replacer of replacers) {
    if (!replacer.match(el)) continue;

    const rendered = replacer.render(el, renderChildren);

    if (rendered !== undefined) return rendered;
  }

  return undefined;
}

/** Кастомный рендер изображения из HTML редактора. */
export type CmsContentImageRenderer = (element: Element) => ReplaceResult;

export interface CmsContentProps {
  /** HTML-строка контента из CMS (Битрикс-редактор) */
  html: string;
  /**
   * Первый видео-плеер этого контента лежит в первом экране — его постер
   * грузится с высоким приоритетом, без `loading="lazy"` (см. `priority`
   * у `LazyIframeVideo`). Ставить только тому `CmsContent`, чей плеер и
   * правда виден без скролла.
   */
  videoPriority?: boolean;
  /**
   * Обернуть каждую таблицу редакторского HTML в `<div class="scroll-wrap">` —
   * на узких экранах широкая таблица скроллится внутри себя, а не растягивает
   * страницу. Стили `.scroll-wrap` задаёт сама страница; опция выключена по
   * умолчанию, чтобы не менять разметку остальным потребителям.
   */
  wrapTables?: boolean;
  /** Кастомный рендер обычных `<img>` из HTML редактора. */
  imageRenderer?: CmsContentImageRenderer;
  /**
   * Шорткоды страницы: имя без решёток → рендер. Каждый `#<имя>#` или
   * `#<имя>_<параметры>#` в тексте редактора заменяется тем, что вернул рендер
   * (`#accordion_2-5#` → блок аккордеона). Новый модуль — просто ещё один ключ.
   * Незнакомые `#...#` остаются в тексте — их вырезает страница.
   */
  shortcodes?: ShortcodeMap;
  /**
   * Правила замены элементов: проверяются по порядку, до встроенных
   * (`.video-frame`, ленивые `img`/`iframe`, обёртка таблиц). Первое
   * подошедшее правило и решает, чем стал элемент.
   */
  elements?: CmsElementReplacer[];
}
